<template>
    <div>
        <div class="flex justify-center w-full">
            <custom-tooltip bottom border>
                <app-button icon circle @click="openNewFilter">
                    <img
                        src="@/assets/images/filter.svg"
                        height="15"
                        width="15"
                        alt="reload img"
                    />
                </app-button>
                <template #tooltip> Modificar filtros de búsqueda </template>
            </custom-tooltip>
        </div>
        <div class="flex flex-wrap w-full">
            <div
                v-if="activeFilters.includes('createdAt')"
                class="w-full px-2 mt-1"
            >
                <dates-filter
                    v-model="createdAt"
                    title="Filtro de fecha de creación"
                    :allow-future-dates="false"
                    default-mode="relative"
                    @change="applyFilter"
                />
            </div>

            <div
                v-if="activeFilters.includes('currentPeriodEnd')"
                class="w-full px-2 mt-1"
            >
                <dates-filter
                    v-model="currentPeriodEnd"
                    title="Filtro de fecha de vencimiento"
                    allow-future-dates
                    default-mode="absolute"
                    @change="applyFilter"
                />
            </div>

            <div
                v-if="activeFilters.includes('name')"
                class="md:w-6/12 px-2 w-full mx-auto mt-2"
            >
                <custom-input
                    v-model="name"
                    type="text"
                    placeholder="Buscar por nombre o slug"
                    @blur="applyFilter"
                    @enter="onEnter"
                >
                    <template #icon> 🔎 </template>
                </custom-input>
            </div>

            <div
                v-if="activeFilters.includes('id')"
                class="md:w-6/12 px-2 w-full mx-auto mt-2"
            >
                <tenant-user-search
                    v-model="user"
                    enabled
                    label="Usuario"
                    @input="applyFilter"
                />
            </div>

            <div
                v-if="activeFilters.includes('planCode')"
                class="md:w-6/12 px-2 w-full mx-auto mt-2"
            >
                <plan-code-selector
                    v-model="planCode"
                    enabled
                    @change="applyFilter"
                />
            </div>

            <div
                v-if="activeFilters.includes('businessType')"
                class="md:w-6/12 px-2 w-full mx-auto mt-2"
            >
                <business-type-selector
                    v-model="businessType"
                    enabled
                    @change="applyFilter"
                />
            </div>

            <div
                v-if="activeFilters.includes('subscriptionType')"
                class="md:w-6/12 px-2 w-full mx-auto mt-2"
            >
                <subscription-type-selector
                    v-model="subscriptionType"
                    enabled
                    @change="applyFilter"
                />
            </div>

            <div
                v-if="activeFilters.includes('subscriptionStatus')"
                class="md:w-6/12 px-2 w-full mx-auto mt-2"
            >
                <subscription-status-selector
                    v-model="subscriptionStatus"
                    enabled
                    @change="applyFilter"
                />
            </div>

            <div
                v-if="activeFilters.includes('lastInvoiceStatus')"
                class="md:w-6/12 px-2 w-full mx-auto mt-2"
            >
                <last-invoice-status-selector
                    v-model="lastInvoiceStatus"
                    enabled
                    @change="applyFilter"
                />
            </div>
        </div>

        <app-dialog v-model="isOpenModal" size="lg" @close="pushNewFilter">
            <template #header>
                <div class="flex items-center justify-between gap-4">
                    <h3 class="text-base font-semibold text-theme-base">
                        Selecciona los filtros
                    </h3>

                    <app-button @click="isOpenModal = false">
                        ✔️ Aplicar filtros ({{ filters.length }})
                    </app-button>
                </div>
            </template>
            <cards-selector v-model="filters" :base="baseFilters" />
        </app-dialog>
    </div>
</template>

<script>
import { tenantsFilters } from "@/common/fields/filters-query";

import BusinessTypeSelector from "@/components/tenants/BusinessTypeSelector.vue";
import LastInvoiceStatusSelector from "@/components/tenants/LastInvoiceStatusSelector.vue";
import PlanCodeSelector from "@/components/tenants/PlanCodeSelector.vue";
import SubscriptionStatusSelector from "@/components/tenants/SubscriptionStatusSelector.vue";
import SubscriptionTypeSelector from "@/components/tenants/SubscriptionTypeSelector.vue";
import TenantUserSearch from "@/components/tenants/TenantUserSearch.vue";
import AppDialog from "@/components/utils/AppDialog.vue";
import CardsSelector from "@/components/utils/CardsSelector.vue";
import CustomInput from "@/components/utils/CustomInput.vue";
import DatesFilter from "@/components/utils/DatesFilter.vue";

export default {
    name: "TenantsFilters",
    components: {
        AppDialog,
        BusinessTypeSelector,
        CardsSelector,
        CustomInput,
        DatesFilter,
        PlanCodeSelector,
        SubscriptionTypeSelector,
        SubscriptionStatusSelector,
        LastInvoiceStatusSelector,
        TenantUserSearch
    },
    model: {
        prop: "itemsCurrent",
        event: "change"
    },
    props: {
        itemsCurrent: {
            default: () => ({}),
            type: Object,
            require: true
        },
        enabledFilters: {
            default: () => [
                "name",
                "id",
                "planCode",
                "businessType",
                "subscriptionType",
                "subscriptionStatus",
                "lastInvoiceStatus",
                "createdAt",
                "currentPeriodEnd"
            ],
            type: Array,
            require: false
        }
    },
    emits: ["change", "enter"],
    data: () => ({
        activeFilters: [],
        filters: [],
        baseFilters: [],
        isOpenModal: false,
        name: "",
        user: { id: "", email: "", tenantId: "" },
        planCode: "",
        businessType: "",
        subscriptionType: "",
        subscriptionStatus: "",
        lastInvoiceStatus: "",
        createdAt: {},
        currentPeriodEnd: {},
        lastEmitted: null
    }),
    watch: {
        filters() {
            this.applyFilter();
        },
        enabledFilters(value) {
            this.setEnabledFilters(value);
        },
        itemsCurrent(value) {
            if (JSON.stringify(value) === this.lastEmitted) return;
            this.setInput(value);
        }
    },
    created() {
        this.setEnabledFilters(this.enabledFilters);
    },
    mounted() {
        this.setInput(this.itemsCurrent);
    },
    methods: {
        setInput(value) {
            const filters = Object.keys(value).filter(
                (k) =>
                    value[k] !== "" &&
                    value[k] !== undefined &&
                    value[k] !== null
            );
            // Solo reasignar si el contenido cambió: reasignar siempre
            // dispararía el watcher de `filters` -> `applyFilter()` ->
            // `change` -> nuevo `itemsCurrent` -> `setInput`, en loop.
            if (
                JSON.stringify(this.activeFilters) !== JSON.stringify(filters)
            ) {
                this.activeFilters = filters;
            }
            if (JSON.stringify(this.filters) !== JSON.stringify(filters)) {
                this.filters = filters;
            }
            if ("name" in value) {
                this.name = value.name;
            }
            // Al recargar desde la URL solo llega el `id` del tenant: se
            // conserva el usuario elegido si es del mismo tenant.
            if ("id" in value && this.user.tenantId !== value.id) {
                this.user = { id: "", email: "", tenantId: value.id };
            }
            if ("planCode" in value) {
                this.planCode = value.planCode;
            }
            if ("businessType" in value) {
                this.businessType = value.businessType;
            }
            if ("subscriptionType" in value) {
                this.subscriptionType = value.subscriptionType;
            }
            if ("subscriptionStatus" in value) {
                this.subscriptionStatus = value.subscriptionStatus;
            }
            if ("lastInvoiceStatus" in value) {
                this.lastInvoiceStatus = value.lastInvoiceStatus;
            }
            if ("createdAt" in value) {
                this.createdAt = value.createdAt;
            }
            if ("currentPeriodEnd" in value) {
                this.currentPeriodEnd = value.currentPeriodEnd;
            }
        },
        openNewFilter() {
            this.isOpenModal = true;
        },
        pushNewFilter() {
            this.activeFilters = [...this.filters];
            this.applyFilter();
        },
        onEnter() {
            this.applyFilter();
            this.$emit("enter");
        },
        applyFilter() {
            const isActive = (key) => this.activeFilters.includes(key);
            const hasRange = (range) =>
                Boolean(range && range.begin && range.end);
            const payload = {
                ...(isActive("name") && this.name ? { name: this.name } : null),
                ...(isActive("id") && this.user.tenantId
                    ? { id: this.user.tenantId }
                    : null),
                ...(isActive("planCode") && this.planCode
                    ? { planCode: this.planCode }
                    : null),
                ...(isActive("businessType") && this.businessType
                    ? { businessType: this.businessType }
                    : null),
                ...(isActive("subscriptionType") && this.subscriptionType
                    ? { subscriptionType: this.subscriptionType }
                    : null),
                ...(isActive("subscriptionStatus") && this.subscriptionStatus
                    ? { subscriptionStatus: this.subscriptionStatus }
                    : null),
                ...(isActive("lastInvoiceStatus") && this.lastInvoiceStatus
                    ? { lastInvoiceStatus: this.lastInvoiceStatus }
                    : null),
                ...(isActive("createdAt") && hasRange(this.createdAt)
                    ? { createdAt: this.createdAt }
                    : null),
                ...(isActive("currentPeriodEnd") &&
                hasRange(this.currentPeriodEnd)
                    ? { currentPeriodEnd: this.currentPeriodEnd }
                    : null)
            };
            this.lastEmitted = JSON.stringify(payload);
            this.$emit("change", payload);
        },
        setEnabledFilters(value) {
            this.baseFilters = tenantsFilters.filter((item) => {
                const hasPermission =
                    !item.permissions ||
                    item.permissions.every((permission) =>
                        this.$ability.hasPermission(permission)
                    );
                return (
                    value.includes(item.id) &&
                    (item.show === undefined || item.show) &&
                    hasPermission
                );
            });
        }
    }
};
</script>
